import { LabelLanguages, LabelRepository, SaveLabelLanguage } from '@telespot/domain';
import { CloudFunctions } from '@telespot/sdk';
import { v4 as uuidv4 } from 'uuid';

import { ListLabelsOutput } from '../../queries/list-labels';

export interface SaveLabelInput {
  uuid?: string;
  /**
   * Localized values keyed by language.
   *
   * Every value is whitespace-normalized before anything else happens —
   * leading/trailing whitespace trimmed and internal runs of whitespace
   * collapsed to a single space — so validation, duplicate detection and
   * persistence all operate on the normalized values. Letter case is preserved
   * exactly as given.
   *
   * English is required and must be non-blank after normalization; otherwise
   * the use case rejects with {@link MissingEnglishValueException}.
   *
   * Other languages are optional, and the same value across several languages
   * is legitimate and common. A key that is present but blank after
   * normalization clears that stored translation; an absent key leaves the
   * stored translation alone.
   */
  values: LabelLanguages;
}

export interface SaveLabelOutput {
  /** The persisted label's uuid. */
  uuid: string;
  /**
   * The whitespace-normalized values that were persisted — the authoritative
   * state of the label after this save. Callers that display the label must
   * use these, not the values they submitted.
   */
  values: LabelLanguages;
}

export interface ConflictingLabelInfo {
  uuid: string;
  values: { lang: SaveLabelLanguage; value: string }[];
}

export abstract class SaveLabelException extends Error {
  protected constructor(message: string) {
    super(message);
    this.name = new.target.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class MissingEnglishValueException extends SaveLabelException {
  constructor() {
    super('English translation is required to save a label.');
  }
}

export class DuplicateLabelException extends SaveLabelException {
  constructor(public readonly conflictingLabel: ConflictingLabelInfo) {
    const englishValue =
      conflictingLabel.values.find((v) => v.lang === 'en')?.value ??
      conflictingLabel.values[0]?.value ??
      '';
    super(
      `A label with a similar value already exists (uuid: ${conflictingLabel.uuid}, value: "${englishValue}").`
    );
  }
}

export interface ISaveLabelCommand {
  execute(input: SaveLabelInput): Promise<SaveLabelOutput>;
}

export class SaveLabelCommand implements ISaveLabelCommand {
  constructor(private readonly labelRepository: LabelRepository) {}

  public async execute(input: SaveLabelInput): Promise<SaveLabelOutput> {
    const normalized = this.normalizeValues(input.values);
    const en = normalized.en;
    if (!en) {
      throw new MissingEnglishValueException();
    }
    const values: LabelLanguages = { ...normalized, en };

    const conflict = await this.findNearDuplicate(values, input.values, input.uuid);
    if (conflict) {
      throw new DuplicateLabelException(conflict);
    }

    const uuid = input.uuid ?? uuidv4();
    await this.labelRepository.save(uuid, values);
    return { uuid, values };
  }

  /**
   * Trims each value and collapses its internal whitespace runs to a single
   * space, preserving letter case. Present-but-blank values stay in the map as
   * empty strings so the repository can clear the stored translation.
   */
  private normalizeValues(
    values: LabelLanguages
  ): Partial<Record<SaveLabelLanguage, string>> {
    return Object.fromEntries(
      Object.entries(values).map(([lang, value]) => [
        lang,
        (value ?? '').trim().replace(/\s+/g, ' '),
      ])
    );
  }

  /**
   * Candidate rows are fetched with search terms built from the values exactly
   * as the caller supplied them, not from the normalized ones: the repository
   * matches a search term as a substring of the value **as stored**, so a row
   * persisted before normalization existed is only reachable through the
   * caller's original spacing. Comparison then normalizes both sides, so which
   * of the two maps seeds it makes no difference.
   */
  private async findNearDuplicate(
    values: SaveLabelInput['values'],
    rawValues: SaveLabelInput['values'],
    ownUuid: string | undefined
  ): Promise<ConflictingLabelInfo | undefined> {
    const inputNormalizedSet = new Set<string>(
      Object.values(values)
        .map((v) => this.normalize(v))
        .filter((v) => v.length > 0)
    );
    if (inputNormalizedSet.size === 0) return undefined;

    const searchTerms = this.buildSearchTerms(rawValues);
    for (const term of searchTerms) {
      const groups = (await CloudFunctions.ListLabels({
        language: 'en',
        searchTerm: term,
        limit: 20,
      })) as ListLabelsOutput;

      for (const [uuid, labels] of Object.entries(groups)) {
        if (uuid === ownUuid) continue;
        const matches = labels.some((label) =>
          inputNormalizedSet.has(this.normalize(label.value))
        );
        if (matches) {
          return {
            uuid,
            values: labels.map((l) => ({ lang: l.lang, value: l.value })),
          };
        }
      }
    }
    return undefined;
  }

  private buildSearchTerms(values: SaveLabelInput['values']): string[] {
    const terms = new Set<string>();
    for (const raw of Object.values(values)) {
      const trimmed = raw?.trim().toLowerCase() ?? '';
      if (!trimmed) continue;
      terms.add(trimmed);
      terms.add(trimmed.replace(/\s+/g, ''));
    }
    return [...terms];
  }

  private normalize(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, '');
  }
}
